import axios from 'axios';

export const API_URL = 'https://makka-labs.com/api';
// export const API_URL = 'https://makka-labs.com/api';
// Flag to prevent multiple redirects
let isRedirecting = false;

const axiosInstance = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// ✅ Request interceptor - only for language header now
axiosInstance.interceptors.request.use(
    (config) => {
        // ❌ REMOVED: No more token from localStorage
        // The cookie is sent automatically with withCredentials: true

        const currentLanguage = localStorage.getItem('i18nextLng') || 'en';
        config.headers['Accept-Language'] = currentLanguage;
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor with detailed logging
axiosInstance.interceptors.response.use(
    (response) => {
        console.log('✅ [API] Response:', response.config.url, response.status);
        return response;
    },
    async (error) => {
        console.log('❌ [API] Error:', error.config?.url, error.response?.status, error.response?.data);

        const originalRequest = error.config;

        const isAuthRoute = originalRequest?.url?.includes('/auth/login') ||
            originalRequest?.url?.includes('/auth/refresh') ||
            originalRequest?.url?.includes('/auth/check') ||
            originalRequest?.url?.includes('/auth/logout');

        console.log('🔍 [API] Is auth route:', isAuthRoute);
        console.log('🔍 [API] Is retry:', originalRequest?._retry);
        console.log('🔍 [API] Is redirecting:', isRedirecting);

        if (
            error.response?.status === 401 &&
            !isAuthRoute &&
            !originalRequest?._retry &&
            !isRedirecting
        ) {
            originalRequest._retry = true;

            try {
                console.log('🔄 [API] Attempting token refresh...');
                await axiosInstance.post('/auth/refresh-token');
                console.log('✅ [API] Token refreshed, retrying original request');
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                console.log('❌ [API] Token refresh failed:', refreshError.response?.status);

                if (!isRedirecting) {
                    isRedirecting = true;

                    setTimeout(() => {
                        isRedirecting = false;
                        window.location.href = '/login';
                    }, 100);
                }
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
// =====================
// HELPER FUNCTIONS
// =====================

/**
 * Helper to build query params safely
 * @param {Object} params - Parameters object
 * @returns {string} - Query string
 */
const buildQueryParams = (params) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value);
        }
    });
    return queryParams.toString();
};

/**
 * Helper to clean select/fields parameters - removes all spaces
 * @param {Object} params - Parameters object
 * @returns {Object} - Cleaned parameters object
 */
const cleanParams = (params) => {
    if (!params) return {};

    const cleaned = { ...params };

    // Clean select parameter - remove all spaces around commas
    if (cleaned.select && typeof cleaned.select === 'string') {
        cleaned.select = cleaned.select.replace(/\s*,\s*/g, ',').replace(/\s+/g, '').trim();
    }

    // Clean fields parameter - remove all spaces around commas
    if (cleaned.fields && typeof cleaned.fields === 'string') {
        cleaned.fields = cleaned.fields.replace(/\s*,\s*/g, ',').replace(/\s+/g, '').trim();
    }

    // Clean populate parameter
    if (cleaned.populate && typeof cleaned.populate === 'string') {
        cleaned.populate = cleaned.populate.replace(/\s*,\s*/g, ',').replace(/\s+/g, '').trim();
    }

    return cleaned;
};

/**
 * Process array parameters - convert arrays to comma-separated strings
 * @param {Object} params - Parameters object
 * @param {Array<string>} arrayFields - Fields that should be converted from arrays
 * @returns {Object} - Processed parameters
 */
const processArrayParams = (params, arrayFields = []) => {
    const processed = cleanParams({ ...params });

    arrayFields.forEach(field => {
        if (Array.isArray(processed[field])) {
            processed[field] = processed[field].join(',');
        }
    });

    return processed;
};

// =====================
// BASE CRUD SERVICE
// =====================

const createCrudService = (endpoint) => ({
    getAll: (params) => axiosInstance.get(`/${endpoint}`, { params: cleanParams(params) }),
    getById: (id) => axiosInstance.get(`/${endpoint}/${id}`),
    create: (data) => axiosInstance.post(`/${endpoint}`, data),
    update: (id, data) => axiosInstance.put(`/${endpoint}/${id}`, data),
    delete: (id) => axiosInstance.delete(`/${endpoint}/${id}`),
    search: (query, options) => axiosInstance.get(`/${endpoint}`, {
        params: cleanParams({ search: query, ...options })
    }),
});

// =====================
// AUTH API
// =====================
export const authAPI = {
    login: (credentials) => axiosInstance.post('/auth/login', credentials),
    register: (userData) => axiosInstance.post('/auth/register', userData),
    logout: () => axiosInstance.post('/auth/logout'),
    refreshToken: () => axiosInstance.post('/auth/refresh-token'),
    getProfile: () => axiosInstance.get('/auth/me'),
    updateProfile: (data) => axiosInstance.patch('/auth/me', data),
    changePassword: (data) => axiosInstance.post('/auth/change-password', data),
    forgotPassword: (email) => axios.post(`${API_URL}/auth/forgot-password`, { email }),
    checkAuth: () => axiosInstance.get('/auth/check'),
    refreshToken: () => axiosInstance.post('/auth/refresh'),
    resetPassword: (token, password) => axios.post(`${API_URL}/auth/reset-password`, { token, password }),
    adminResetPassword: (userId, newPassword) =>
        axiosInstance.post(`/auth/admin/reset-password/${userId}`, { newPassword }),
    checkAdminStatus: () => axiosInstance.get('/auth/check-admin'),
};

// =====================
// USER API
// =====================
export const userAPI = {
    getAll: (params) => axiosInstance.get('/users', { params: cleanParams(params) }),
    getById: (id) => axiosInstance.get(`/users/${id}`),
    update: (id, data) => axiosInstance.patch(`/users/${id}`, data),
    delete: (id) => axiosInstance.delete(`/users/${id}`),
    grantPrivileges: (userId, data) => axiosInstance.post(`/users/${userId}/privileges`, data),
    revokePrivileges: (userId, data) => axiosInstance.delete(`/users/${userId}/privileges`, { data }),
    getStats: (id, params) => axiosInstance.get(`/users/${id}/stats`, { params }),

    // Radiologist specific
    getRadiologists: (params = {}) => axiosInstance.get('/users/radiologists', { params: cleanParams(params) }),
    updateRadiologist: (id, data) => axiosInstance.patch(`/users/radiologist/${id}`, data),

    // Radiologist Statistics with pagination
    getRadiologistStatistics: (id, params = {}) => {
        const queryString = buildQueryParams({
            startDate: params.startDate,
            endDate: params.endDate,
            branchId: params.branchId,
            scanCategoryId: params.scanCategoryId,
            scanId: params.scanId,
            patientId: params.patientId,
            status: params.status,
            page: params.page,
            limit: params.limit,
        });

        return axiosInstance.get(`/users/radiologists/${id}/statistics?${queryString}`);
    },

    getRadiologistStatsFilterOptions: (id) => {
        return axiosInstance.get(`/users/radiologists/${id}/statistics/filters`);
    },

    // Radiologist Export Methods
    exportRadiologists: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            isActive: params.isActive,
            gender: params.gender,
            branch: params.branch,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            exportFormat: params.exportFormat,
        });

        return axiosInstance.get(`/users/radiologists/export?${queryString}`, {
            responseType: 'blob'
        });
    },

    exportRadiologistsDetailed: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            isActive: params.isActive,
            gender: params.gender,
            branch: params.branch,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/users/radiologists/export/detailed?${queryString}`, {
            responseType: 'blob'
        });
    },

    exportRadiologistsByGender: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            isActive: params.isActive,
            gender: params.gender,
            branch: params.branch,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/users/radiologists/export/by-gender?${queryString}`, {
            responseType: 'blob'
        });
    },

    exportRadiologistsByBranch: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            isActive: params.isActive,
            gender: params.gender,
            branch: params.branch,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/users/radiologists/export/by-branch?${queryString}`, {
            responseType: 'blob'
        });
    },

    exportRadiologistsByStatus: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            isActive: params.isActive,
            gender: params.gender,
            branch: params.branch,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/users/radiologists/export/by-status?${queryString}`, {
            responseType: 'blob'
        });
    }
};

// =====================
// META API
// =====================
export const metaAPI = {
    getPrivilegeModules: () => axiosInstance.get('/meta/privileges'),
    getBranches: async () => {
        try {
            const response = await axiosInstance.get('/branches/active');
            if (Array.isArray(response.data?.data)) {
                return response.data.data;
            } else if (Array.isArray(response.data?.data?.branches)) {
                return response.data.data.branches;
            }
            return [];
        } catch (error) {
            console.error('Error fetching branches:', error);
            return [];
        }
    },
};

// =====================
// AUDIT API
// =====================
export const auditAPI = {
    getAll: (params) => axiosInstance.get('/audit', { params: cleanParams(params) }),
    getAppointmentLogs: (params) => axiosInstance.get('/audit/appointments', { params: cleanParams(params) }),
    getAuditStats: (params) => axiosInstance.get('/audit/stats', { params: cleanParams(params) }),
};

// =====================
// DASHBOARD API
// =====================
export const dashboardAPI = {
    getAnalytics: (params = {}) => axiosInstance.get('/dashboard/analytics', { params: cleanParams(params) }),
    getAppointmentStats: (period = 'week') => axiosInstance.get('/dashboard/appointment-stats', { params: { period } }),
};

// =====================
// PATIENT API
// =====================
export const patientAPI = {
    ...createCrudService('patients'),

    // Override getAll to handle array parameters and clean select/fields
    getAll: (params = {}) => {
        const processedParams = processArrayParams(params, ['doctorReferredIds']);
        return axiosInstance.get('/patients', { params: processedParams });
    },

    // Get single patient by ID
    getOne: (id) => axiosInstance.get(`/patients/${id}`),

    // Main export with format option
    export: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            gender: params.gender,
            doctorReferred: params.doctorReferred,
            doctorReferredIds: Array.isArray(params.doctorReferredIds)
                ? params.doctorReferredIds.join(',')
                : params.doctorReferredIds,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            exportFormat: params.exportFormat,
        });

        return axiosInstance.get(`/patients/export?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export with detailed patient info (includes medical history)
    exportDetailed: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            gender: params.gender,
            doctorReferred: params.doctorReferred,
            doctorReferredIds: Array.isArray(params.doctorReferredIds)
                ? params.doctorReferredIds.join(',')
                : params.doctorReferredIds,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/patients/export/detailed?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export grouped by referring doctor
    exportByDoctor: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            gender: params.gender,
            doctorReferred: params.doctorReferred,
            doctorReferredIds: Array.isArray(params.doctorReferredIds)
                ? params.doctorReferredIds.join(',')
                : params.doctorReferredIds,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/patients/export/by-doctor?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export grouped by gender
    exportByGender: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            gender: params.gender,
            doctorReferred: params.doctorReferred,
            doctorReferredIds: Array.isArray(params.doctorReferredIds)
                ? params.doctorReferredIds.join(',')
                : params.doctorReferredIds,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/patients/export/by-gender?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export grouped by age
    exportByAge: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            gender: params.gender,
            doctorReferred: params.doctorReferred,
            doctorReferredIds: Array.isArray(params.doctorReferredIds)
                ? params.doctorReferredIds.join(',')
                : params.doctorReferredIds,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/patients/export/by-age?${queryString}`, {
            responseType: 'blob'
        });
    }
};

// =====================
// PATIENT HISTORY API
// =====================
export const patientHistoryAPI = {
    ...createCrudService('patient-history'),
    getByPatientId: (patientId) => axiosInstance.get(`/patient-history/patient/${patientId}`),
};

// =====================
// EXPENSE CATEGORY API
// =====================
export const expenseCategoryAPI = createCrudService('expense-categories');

// =====================
// DOCTOR API
// =====================
const baseDoctorAPI = createCrudService('doctors');

export const doctorAPI = {
    ...baseDoctorAPI,

    //   FIXED: Get single doctor by ID - proper endpoint
    getOne: (id) => axiosInstance.get(`/doctors/${id}`),

    // Override getAll to handle array parameters and clean select/fields
    getAll: (params = {}) => {
        const processedParams = processArrayParams(params, ['representativeIds', 'specializationIds']);
        return axiosInstance.get('/doctors', { params: processedParams });
    },

    // Search doctors with cleaned params
    search: (searchTerm, options = {}) => {
        const processedParams = processArrayParams(
            { search: searchTerm, ...options },
            ['representativeIds', 'specializationIds']
        );
        return axiosInstance.get('/doctors', { params: processedParams });
    },
    // Add these to the doctorAPI object in services/api.js

    // ── Clinic sub-resource ──────────────────────────────────────────────────────
    getClinics: (doctorId) =>
        axiosInstance.get(`/doctors/${doctorId}/clinics`),

    addClinic: (doctorId, clinicData) =>
        axiosInstance.post(`/doctors/${doctorId}/clinics`, clinicData),

    updateClinic: (doctorId, clinicId, clinicData) =>
        axiosInstance.put(`/doctors/${doctorId}/clinics/${clinicId}`, clinicData),

    removeClinic: (doctorId, clinicId) =>
        axiosInstance.delete(`/doctors/${doctorId}/clinics/${clinicId}`),

    // Main export with format option
    export: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            specialization: params.specialization,
            representative: params.representative,
            specializationIds: Array.isArray(params.specializationIds)
                ? params.specializationIds.join(',')
                : params.specializationIds,
            representativeIds: Array.isArray(params.representativeIds)
                ? params.representativeIds.join(',')
                : params.representativeIds,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            exportFormat: params.exportFormat,
        });

        return axiosInstance.get(`/doctors/export?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export with detailed doctor info
    exportDetailed: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            specialization: params.specialization,
            representative: params.representative,
            specializationIds: Array.isArray(params.specializationIds)
                ? params.specializationIds.join(',')
                : params.specializationIds,
            representativeIds: Array.isArray(params.representativeIds)
                ? params.representativeIds.join(',')
                : params.representativeIds,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/doctors/export/detailed?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export grouped by specialization
    exportBySpecialization: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            specialization: params.specialization,
            representative: params.representative,
            specializationIds: Array.isArray(params.specializationIds)
                ? params.specializationIds.join(',')
                : params.specializationIds,
            representativeIds: Array.isArray(params.representativeIds)
                ? params.representativeIds.join(',')
                : params.representativeIds,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/doctors/export/by-specialization?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export grouped by representative
    exportByRepresentative: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            specialization: params.specialization,
            representative: params.representative,
            specializationIds: Array.isArray(params.specializationIds)
                ? params.specializationIds.join(',')
                : params.specializationIds,
            representativeIds: Array.isArray(params.representativeIds)
                ? params.representativeIds.join(',')
                : params.representativeIds,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/doctors/export/by-representative?${queryString}`, {
            responseType: 'blob'
        });
    }
};

// =====================
// TECHNICIAN API
// =====================
export const technicianAPI = {
    ...createCrudService('radiology-technicians'),

    // Override getAll with cleaned params
    getAll: (params = {}) => {
        return axiosInstance.get('/radiology-technicians', { params: cleanParams(params) });
    },

    // Main export with format option
    export: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            status: params.status,
            isActive: params.isActive,
            gender: params.gender,
            shift: params.shift,
            department: params.department,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            exportFormat: params.exportFormat,
        });

        return axiosInstance.get(`/radiology-technicians/export?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export with detailed technician info
    exportDetailed: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            status: params.status,
            isActive: params.isActive,
            gender: params.gender,
            shift: params.shift,
            department: params.department,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/radiology-technicians/export/detailed?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export grouped by shift
    exportByShift: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            status: params.status,
            isActive: params.isActive,
            gender: params.gender,
            shift: params.shift,
            department: params.department,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/radiology-technicians/export/by-shift?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export grouped by gender
    exportByGender: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            status: params.status,
            isActive: params.isActive,
            gender: params.gender,
            shift: params.shift,
            department: params.department,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/radiology-technicians/export/by-gender?${queryString}`, {
            responseType: 'blob'
        });
    }
};

// =====================
// APPOINTMENT API
// =====================
export const appointmentAPI = {
    getAllAppointments: (params) => axiosInstance.get('/appointments', { params: cleanParams(params) }),
    getAppointmentById: (id) => axiosInstance.get(`/appointments/${id}`),
    createAppointment: (appointmentData) => axiosInstance.post('/appointments', appointmentData),
    updateAppointment: (id, appointmentData) => axiosInstance.put(`/appointments/${id}`, appointmentData),
    updateAppointmentStatus: (id, statusData, isFormData = false) => {
        const headers = isFormData ? { 'Content-Type': 'multipart/form-data' } : {};
        return axiosInstance.patch(`/appointments/${id}/status`, statusData, { headers });
    },
    deleteAppointment: (id) => axiosInstance.delete(`/appointments/${id}`),
    getAppointmentHistory: (id) => axiosInstance.get(`/appointments/${id}/history`),
    getByDateRange: (startDate, endDate) => axiosInstance.get('/appointments/date-range', { params: { startDate, endDate } }),
    getByDoctor: (doctorId) => axiosInstance.get(`/appointments/doctor/${doctorId}`),
    getByPatient: (patientId) => axiosInstance.get(`/appointments/patient/${patientId}`),
    updateAppointmentPaymentStatus: (id, paymentData) => axiosInstance.patch(`/appointments/${id}/payment-status`, paymentData),
    getFinancials: (params) => axiosInstance.get('/appointments/financials', { params: cleanParams(params) }),
};

// =====================
// STOCK API
// =====================
export const stockAPI = {
    // ── Core CRUD ────────────────────────────────────────────────────────
    getAll: (params) => axiosInstance.get('/stock', { params: cleanParams(params) }),
    getById: (id) => axiosInstance.get(`/stock/${id}`),
    create: (data) => axiosInstance.post('/stock', data),
    update: (id, data) => axiosInstance.patch(`/stock/${id}`, data),
    delete: (id) => axiosInstance.delete(`/stock/${id}`),
    search: (query) => axiosInstance.get('/stock', { params: cleanParams({ search: query }) }),
    getLowStock: (params) => axiosInstance.get('/stock/low-stock', { params: cleanParams(params) }),
    getExpired: (params) => axiosInstance.get('/stock/expired', { params: cleanParams(params) }),
    updateQuantity: (id, data) => axiosInstance.patch(`/stock/${id}/quantity`, data),
    getAuditTrail: (id) => axiosInstance.get(`/stock/${id}/audit`),
    // ── Daily Snapshots ──────────────────────────────────────────────────
    // branchId is a QUERY parameter, not a URL parameter
    getBranchDailySnapshots: (stockId, branchId, params = {}) =>
        axiosInstance.get(`/stock/${stockId}/daily-snapshots`, {
            params: cleanParams({ branchId, ...params })
        }),

    // ── WAC (Weighted Average Cost) ──────────────────────────────────────
    // branchId is a QUERY parameter, not a URL parameter
    getBranchWACHistory: (stockId, branchId, params = {}) =>
        axiosInstance.get(`/stock/${stockId}/wac-history`, {
            params: cleanParams({ branchId, ...params })
        }),

    getHistoricalWAC: (stockId, branchId, date) =>
        axiosInstance.get(`/stock/${stockId}/wac-at-date`, {
            params: cleanParams({ branchId, date })
        }),

    getCurrentBranchWACs: (branchId) =>
        axiosInstance.get('/stock/branch-wacs', {
            params: cleanParams({ branchId })
        }),

    // ── Transfer History (read-only) ─────────────────────────────────────
    getStockTransferHistory: (stockId, params = {}) =>
        axiosInstance.get(`/stock/${stockId}/transfers`, { params: cleanParams(params) }),

    getBranchTransferHistory: (branchId, params = {}) =>
        axiosInstance.get('/stock/branch-transfers', {
            params: cleanParams({ branchId, ...params })
        }),

    // ── Physical Count ───────────────────────────────────────────────────
    recordPhysicalCount: (stockId, data) =>
        axiosInstance.post(`/stock/${stockId}/physical-count`, data),

    // ── Export ────────────────────────────────────────────────────────────
    export: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            name: params.name,
            branch: params.branch,
            category: params.category,
            lowStock: params.lowStock ? 'true' : undefined,
            expired: params.expired ? 'true' : undefined,
            active: params.active,
        });

        return axiosInstance.get(`/stock/export?${queryString}`, {
            responseType: 'blob'
        });
    }
};
// =====================
// SCAN API
// =====================
export const scanAPI = {
    ...createCrudService('scans'),

    // Override getAll with cleaned params
    getAll: (params = {}) => {
        return axiosInstance.get('/scans', { params: cleanParams(params) });
    },

    update: (id, data) => axiosInstance.patch(`/scans/${id}`, data),
    getByDoctor: (doctorId) => axiosInstance.get(`/scans/doctor/${doctorId}`),
    getByPatient: (patientId) => axiosInstance.get(`/scans/patient/${patientId}`),
    addImage: (id, data) => axiosInstance.post(`/scans/${id}/images`, data),
    removeImage: (id, imageId) => axiosInstance.delete(`/scans/${id}/images/${imageId}`),
    checkStockAvailability: (id) => axiosInstance.get(`/scans/${id}/stock-availability`),
    restore: (id) => axiosInstance.put(`/scans/${id}/restore`),

    // Main export with format option
    export: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            category: params.category,
            branchId: params.branchId,
            branch: params.branch,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            exportFormat: params.exportFormat,
        });

        return axiosInstance.get(`/scans/export?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export with detailed branch pricing (2 sheets)
    exportDetailed: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            category: params.category,
            branchId: params.branchId,
            branch: params.branch,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/scans/export/detailed?${queryString}`, {
            responseType: 'blob'
        });
    },

    // Export with dynamic branch columns
    exportByBranch: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            category: params.category,
            branchId: params.branchId,
            branch: params.branch,
            isActive: params.isActive,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/scans/export/by-branch?${queryString}`, {
            responseType: 'blob'
        });
    }
};

// =====================
// SCAN CATEGORY API
// =====================
export const scanCategoryAPI = {
    ...createCrudService('scan-categories'),

    // Override getAll with cleaned params
    getAll: (params = {}) => {
        return axiosInstance.get('/scan-categories', { params: cleanParams(params) });
    },

    getStats: () => axiosInstance.get('/scan-categories/stats'),
    getActive: () => axiosInstance.get('/scan-categories/active'),
    restoreCategory: (id) => axiosInstance.put(`/scan-categories/${id}/restore`),

    export: (params = {}) => {
        const queryString = buildQueryParams({
            search: params.search,
            status: params.status !== 'all' ? params.status : undefined,
            isActive: params.isActive !== 'all' ? params.isActive : undefined,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });

        return axiosInstance.get(`/scan-categories/export?${queryString}`, {
            responseType: 'blob'
        });
    }
};

// =====================
// EXPENSE API
// =====================
// Add these methods to your expenseAPI object in services/api.js

// Add to your expenseAPI object in services/api.js

export const expenseAPI = {
    getAll: (params) => axiosInstance.get('/expenses', { params }),
    getById: (id) => axiosInstance.get(`/expenses/${id}`),
    create: (data) => axiosInstance.post('/expenses', data),
    update: (id, data) => axiosInstance.put(`/expenses/${id}`, data),
    delete: (id, data = {}) => axiosInstance.delete(`/expenses/${id}`, { data }),

    // Deleted / Trash
    getDeleted: (params) => axiosInstance.get('/expenses/deleted', { params }),
    restore: (id) => axiosInstance.put(`/expenses/${id}/restore`),
    permanentDelete: (id) => axiosInstance.delete(`/expenses/${id}/permanent`),

    // Status actions
    approve: (id) => axiosInstance.put(`/expenses/${id}/approve`),
    reject: (id) => axiosInstance.put(`/expenses/${id}/reject`),
    markAsPaid: (id) => axiosInstance.put(`/expenses/${id}/markAsPaid`),

    // Export
    export: (params) => axiosInstance.get('/expenses/export', {
        params,
        responseType: 'arraybuffer',
    }),

    // Audit Trail
    getAuditTrail: (id, params) => axiosInstance.get(`/expenses/${id}/audit`, { params }),
    getAllAuditLogs: (params) => axiosInstance.get('/expenses/audit-logs', { params }),
};
// =====================
// BRANCH API
// =====================
export const branchAPI = {
    ...createCrudService('branches'),

    // Override getAll with cleaned params
    getAll: (params = {}) => {
        return axiosInstance.get('/branches', { params: cleanParams(params) });
    },

    getActiveBranches: () => axiosInstance.get('/branches/active'),
};

// =====================
// DOCTOR SPECIALIZATION API
// =====================
export const doctorSpecializationAPI = {
    getAll: (params) => axiosInstance.get('/doctor-specializations', { params: cleanParams(params) }),
    getById: (id) => axiosInstance.get(`/doctor-specializations/${id}`),
};

// =====================
// REPRESENTATIVE API
// =====================


// =====================
// REPORT API
// =====================
export const reportAPI = {
    downloadExcelReport: (filters) => {
        const queryParams = new URLSearchParams(
            Object.entries(filters).filter(([, value]) => value)
        );
        return axiosInstance.get(`/reports/appointments/excel?${queryParams.toString()}`, {
            responseType: 'blob'
        });
    }
};

// =====================
// NOTIFICATION API
// =====================
export const notificationAPI = {
    getAll: (params) => axiosInstance.get('/notifications', { params: cleanParams(params) }),
    getUnreadCount: () => axiosInstance.get('/notifications/unread-count'),
    markAsRead: (id) => axiosInstance.put(`/notifications/${id}/read`),
    markAllAsRead: () => axiosInstance.put('/notifications/read-all'),
    delete: (id) => axiosInstance.delete(`/notifications/${id}`)
};


// =====================
// STOCK CATEGORY API
// =====================
export const stockCategoryAPI = {
    getAll: (params = {}) =>
        axiosInstance.get('/stock-categories', { params: cleanParams(params) }),

    getById: (id) =>
        axiosInstance.get(`/stock-categories/${id}`),

    create: (data) =>
        axiosInstance.post('/stock-categories', data),

    update: (id, data) =>
        axiosInstance.put(`/stock-categories/${id}`, data),

    delete: (id) =>
        axiosInstance.delete(`/stock-categories/${id}`),
};

// =====================
// SUPPLIER API
// =====================
export const supplierAPI = {
    getAll: (params = {}) =>
        axiosInstance.get('/suppliers', { params: cleanParams(params) }),

    getById: (id) =>
        axiosInstance.get(`/suppliers/${id}`),

    create: (data) =>
        axiosInstance.post('/suppliers', data),

    update: (id, data) =>
        axiosInstance.put(`/suppliers/${id}`, data),

    delete: (id) =>
        axiosInstance.delete(`/suppliers/${id}`),
};

// =====================
// STOCK TRANSFER API
// =====================
export const stockTransferAPI = {
    // ── Transfer Operations ──────────────────────────────────────────────
    transferCentralToBranch: (data) =>
        axiosInstance.post('/stock-transfers/central-to-branch', data),

    transferBranchToBranch: (data) =>
        axiosInstance.post('/stock-transfers/branch-to-branch', data),

    // ── Query Operations ─────────────────────────────────────────────────
    getAll: (params = {}) =>
        axiosInstance.get('/stock-transfers', { params: cleanParams(params) }),

    getById: (id) =>
        axiosInstance.get(`/stock-transfers/${id}`),

    getByStockItem: (stockItemId, params = {}) =>
        axiosInstance.get(`/stock-transfers/stock-item/${stockItemId}`, { params: cleanParams(params) }),

    getByBranch: (branchId, params = {}) =>
        axiosInstance.get(`/stock-transfers/branch/${branchId}`, { params: cleanParams(params) }),

    // ── Status Management ────────────────────────────────────────────────
    updateStatus: (id, data) =>
        axiosInstance.patch(`/stock-transfers/${id}/status`, data),

    // ── Statistics ────────────────────────────────────────────────────────
    getStats: (params = {}) =>
        axiosInstance.get('/stock-transfers/stats', { params: cleanParams(params) }),
};

// =====================
// ORDER LIST API
// =====================
export const orderListAPI = {
    getAll: (params = {}) =>
        axiosInstance.get('/order-list', { params: cleanParams(params) }),

    getById: (id) =>
        axiosInstance.get(`/order-list/${id}`),

    create: (data) =>
        axiosInstance.post('/order-list', data),

    update: (id, data) =>
        axiosInstance.put(`/order-list/${id}`, data),

    approve: (id, data = {}) =>
        axiosInstance.post(`/order-list/${id}/approve`, data),

    decline: (id, data = {}) =>
        axiosInstance.post(`/order-list/${id}/decline`, data),

    delete: (id) =>
        axiosInstance.delete(`/order-list/${id}`),

    // ── Payment endpoints (ENHANCED) ─────────────────────────────────────
    addPayment: (id, paymentData) =>
        axiosInstance.post(`/order-list/${id}/payment`, paymentData),

    getOrderPayments: (id, params = {}) =>
        axiosInstance.get(`/order-list/${id}/payments`, { params: cleanParams(params) }),

    getAllPayments: (params = {}) =>
        axiosInstance.get('/order-list/payments/all', { params: cleanParams(params) }),

    getPaymentById: (paymentId) =>
        axiosInstance.get(`/order-list/payments/${paymentId}`),

    getSupplierPaymentSummary: (supplierId) =>
        axiosInstance.get(`/order-list/supplier/${supplierId}/payment-summary`),
};

export { axiosInstance };